import { useEffect, useState } from 'react';
import { Printer, Download, FileSpreadsheet } from 'lucide-react';
import type { CatererFormat, EscortCardLayout, PrintPaper, PrintableKind } from '@peles/shared';
import { EntityModal } from '../common/EntityModal';
import { SegmentedControl } from '../common/SegmentedControl';
import { SearchableListSelect } from '../common/SearchableListSelect';
import { useInviteCampaigns } from '../../hooks/useInvites';
import { useDialog } from '../../context/DialogContext';
import { downloadPrintable, triggerDownload, type PrintableParams } from '../../api/export';
import { extractApiErrorMessage } from '../../utils/apiError';

const PAPERS: { id: PrintPaper; label: string }[] = [
  { id: 'letter', label: 'Letter' },
  { id: 'a4', label: 'A4' },
];

const LAYOUTS: { id: EscortCardLayout; label: string }[] = [
  { id: 'cut', label: 'Cut by hand' },
  { id: 'avery-5371', label: 'Avery 5371' },
];

/** One printable on the list: what it is, its options, and its download. */
function PrintableCard({
  title,
  description,
  children,
  actions,
}: {
  title: string;
  description: string;
  children?: React.ReactNode;
  actions: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-gray-200 p-4 space-y-3" aria-label={title}>
      <div>
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        <p className="text-xs text-gray-500">{description}</p>
      </div>
      {children}
      <div className="flex flex-wrap gap-2">{actions}</div>
    </section>
  );
}

function DownloadButton({
  label,
  busy,
  onClick,
  icon = 'pdf',
}: {
  label: string;
  busy: boolean;
  onClick: () => void;
  icon?: 'pdf' | 'xlsx';
}) {
  const Icon = icon === 'xlsx' ? FileSpreadsheet : Download;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className="inline-flex min-h-11 sm:min-h-0 items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-2 text-sm text-white hover:bg-primary-700 disabled:opacity-50"
    >
      <Icon size={14} aria-hidden />
      {busy ? 'Preparing…' : label}
    </button>
  );
}

/**
 * Which invitation's shared link the table cards' QR code opens — offered
 * only when the event has more than one; left alone it is the newest.
 */
function SharedLinkChoice({
  eventId,
  value,
  onChange,
}: {
  eventId: string;
  value: string;
  onChange: (campaignId: string) => void;
}) {
  const { data: campaigns = [], isSuccess } = useInviteCampaigns(eventId);
  const shared = campaigns.filter((campaign) => campaign.publicToken);
  // A choice that is no longer on offer (its link was switched off, or the
  // list is another event's) is let go, so the cards fall back to the newest
  // rather than asking for a link that is gone and printing no QR code.
  const stale = isSuccess && !!value && (shared.length < 2 || !shared.some((c) => c.id === value));
  useEffect(() => {
    if (stale) onChange('');
  }, [stale, onChange]);
  if (shared.length < 2) return null;
  return (
    <div className="pl-6">
      <SearchableListSelect
        ariaLabel="Invitation for the QR code"
        items={shared}
        value={value || null}
        onChange={(next) => onChange(typeof next === 'string' ? next : '')}
        getValue={(campaign) => campaign.id}
        getLabel={(campaign) => campaign.name}
        placeholder="The newest invitation"
        clearable
      />
    </div>
  );
}

/**
 * The day's printables (redesign Phase 8) — escort cards, place cards, table
 * cards and the caterer sheet — each drawn by the server from the event's
 * seating plan. The button opens a dialog with each one's options.
 */
export function PrintablesButton({
  eventId,
  chooseInvitation = false,
}: {
  eventId: string;
  /** Offer a choice of invitation for the QR code — the office's, who can list them. */
  chooseInvitation?: boolean;
}) {
  const { alert } = useDialog();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [paper, setPaper] = useState<PrintPaper>('letter');
  const [layout, setLayout] = useState<EscortCardLayout>('cut');
  const [showMeal, setShowMeal] = useState(false);
  const [qr, setQr] = useState(true);
  const [campaignId, setCampaignId] = useState('');
  // A choice belongs to one event's invitations.
  useEffect(() => setCampaignId(''), [eventId]);

  const download = async (
    key: string,
    kind: PrintableKind,
    params: PrintableParams,
    filename: string,
  ) => {
    setBusy(key);
    try {
      triggerDownload(await downloadPrintable(eventId, kind, params), filename);
    } catch (err) {
      await alert({
        message: extractApiErrorMessage(err, 'Could not prepare the file'),
        variant: 'error',
      });
    } finally {
      setBusy(null);
    }
  };

  const caterer = (format: CatererFormat) =>
    download(`caterer-${format}`, 'caterer', { format }, `caterer-sheet.${format}`);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex min-h-11 sm:min-h-0 items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm hover:bg-gray-50"
      >
        <Printer size={15} aria-hidden />
        Print
      </button>
      <EntityModal open={open} onClose={() => setOpen(false)} title="Print for the day" size="md">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Everything here is drawn from the event&apos;s seating plan, as it stands now.
          </p>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-700">Paper</span>
            <SegmentedControl
              ariaLabel="Paper"
              options={PAPERS}
              value={paper}
              onChange={setPaper}
            />
          </div>

          <PrintableCard
            title="Escort cards"
            description="One card for each party, with the table it sits at, in alphabetical order — ten to a sheet."
            actions={
              <DownloadButton
                label="Download escort cards"
                busy={busy === 'escort'}
                onClick={() =>
                  void download('escort', 'escort-cards', { paper, layout }, 'escort-cards.pdf')
                }
              />
            }
          >
            <SegmentedControl
              ariaLabel="Escort card layout"
              variant="chips"
              options={LAYOUTS}
              value={layout}
              onChange={setLayout}
            />
            {layout === 'avery-5371' && paper === 'a4' && (
              <p className="text-xs text-amber-700">
                Avery 5371 is a Letter stock — it prints on Letter.
              </p>
            )}
          </PrintableCard>

          <PrintableCard
            title="Place cards"
            description="A tent card for each seat, with the person's name, four to a sheet — fold along the dotted line."
            actions={
              <DownloadButton
                label="Download place cards"
                busy={busy === 'place'}
                onClick={() =>
                  void download(
                    'place',
                    'place-cards',
                    { paper, meal: showMeal },
                    'place-cards.pdf',
                  )
                }
              />
            }
          >
            <label className="flex min-h-11 sm:min-h-0 items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={showMeal}
                onChange={(e) => setShowMeal(e.target.checked)}
                className="rounded border-gray-300"
              />
              Show each person&apos;s meal
            </label>
          </PrintableCard>

          <PrintableCard
            title="Table cards"
            description="A tent card for each table, with its number and name, one to a sheet."
            actions={
              <DownloadButton
                label="Download table cards"
                busy={busy === 'table'}
                onClick={() =>
                  void download(
                    'table',
                    'table-cards',
                    { paper, qr, ...(qr && campaignId ? { campaignId } : {}) },
                    'table-cards.pdf',
                  )
                }
              />
            }
          >
            <label className="flex min-h-11 sm:min-h-0 items-start gap-2 py-2 sm:py-0 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={qr}
                onChange={(e) => setQr(e.target.checked)}
                className="mt-0.5 rounded border-gray-300"
              />
              <span>
                QR code to the event&apos;s shared invitation link
                <span className="block text-xs text-gray-500">
                  The newest invitation with a shared link — its programme and directions. Left off
                  when there is none.
                </span>
              </span>
            </label>
            {chooseInvitation && open && qr && (
              <SharedLinkChoice eventId={eventId} value={campaignId} onChange={setCampaignId} />
            )}
          </PrintableCard>

          <PrintableCard
            title="Caterer sheet"
            description="Meals by table, children, and every dietary need by name and seat."
            actions={
              <>
                <DownloadButton
                  label="PDF"
                  busy={busy === 'caterer-pdf'}
                  onClick={() => void caterer('pdf')}
                />
                <DownloadButton
                  label="Excel"
                  icon="xlsx"
                  busy={busy === 'caterer-xlsx'}
                  onClick={() => void caterer('xlsx')}
                />
              </>
            }
          />
        </div>
      </EntityModal>
    </>
  );
}
